<template>
  <div class="popUpWrap dark" style="z-index: 1999;">
    <section class="popup dark invite">
      <div class="topArea">
        <div class="exit" @click.stop="onClickClose"></div>
      </div>
      <div class="scrollY">
        <div class="icon"></div>
        <h5>{{ t('skillCertify-invite-candidates') }}<span>{{ emailList.length }}</span></h5>
        <span class="lineContent" />
        <p v-html="invitationContent" />
        <div class="inputWrap scrollY">
          <input v-model="emailInputData" type="text" :placeholder="t('account-email')" @keydown.enter="onKeyDownEnterEmailData" @keyup="onKeyUpCommasEmailData">
        </div>
        <div v-if="emailList.length > 0" class="inputWrap scrollY" :class="{ error: !isCheckValidEmailList }">
          <div
            v-for="(email, index) in emailList"
            :key="index"
            class="email"
            :class="{ error: isValidEmail(email), already: skippedEmails.includes(email) }">
            <p>{{ email }}</p>
            <span @click.stop="onClickDeleteEmail(index)"></span>
          </div>
        </div>
        <p v-if="showDuplicateWarning" class="errorMessage">
          <span class="icon"></span>
          {{ t("skillCertify-invite-candidates-duplicate-warning") }}
        </p>
        <span class="lineContent"></span>
        <SendInvitationPopupCreditSummary
          :candidate-count="emailList.length"
          :credit-per-candidate="creditPerCandidate"
        />
        <div class="btnArea">
          <LazySafetyRequestButton tag="button" :class="{ disabled : !isCheckValidEmailList || emailList.length === 0 }" :safety-click="addCandidate">
            {{ t('common-invite') }}
          </LazySafetyRequestButton>
        </div>
      </div>
    </section>
    <div v-if="isOpenLimitAlert" class="popUpWrap">
      <section class="popup dark basic">
        <div class="topArea">
          <div class="exit" @click.stop="onCloseLimitAlert"></div>
        </div>
        <div class="scrollY">
          <div class="icon finish"></div>
          <h5>{{ t("credit-certify-invitation-limit") }}</h5>
          <p v-html="alertContent" />
          <div class="btnArea">
            <button @click.stop="onConfirmLimitAlert">
              {{ t("credit-certify-creation-limit-button") }}
            </button>
          </div>
        </div>
      </section>
    </div>
    <div v-if="isOpenSubscriptionErrorAlert" class="popUpWrap">
      <section class="popup dark basic">
        <div class="topArea">
          <div class="exit" @click.stop="onCloseSubscriptionErrorAlert"></div>
        </div>
        <div class="scrollY">
          <div class="icon finish"></div>
          <h5>{{ t("credit-certify-invitation-restriction-info-title") }}</h5>
          <p>{{ t('credit-history-top-up-credits-restriction-info-2-description') }}</p>
          <div class="btnArea">
            <button @click.stop="onConfirmSubscriptionErrorAlert">
              {{ t("payment-complete-button") }}
            </button>
          </div>
        </div>
      </section>
    </div>
    <div v-if="isOpenDuplicateConfirm" class="popUpWrap">
      <section class="popup dark basic">
        <div class="topArea">
          <div class="exit" @click.stop="onCloseDuplicateConfirm"></div>
        </div>
        <div class="scrollY">
          <div class="textArea flex">
            <h5 v-html="convertToNextLine(t('skillCertify-invite-candidates-duplicate-count', { count: duplicateCount }))" />
            <p>{{ t('skillCertify-invite-candidates-duplicate-description') }}</p>
          </div>
          <div class="btnArea">
            <button class="cancel" @click.stop="onCloseDuplicateConfirm">{{ t('common-cancel') }}</button>
            <button @click.stop="onConfirmDuplicateInvite">{{ t('common-invite') }}</button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { emailValidation } from "~/utils/ValidationUtil";
import { useDialogStore } from "~~/application/store/dialog/dialogStore";
import { useGenericDialog } from "~/composables/useGenericDialog";
import { useProSkillAssureService } from "~/composables/service/useProSkillAssureService";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { convertToNextLine } from "~/utils/StringUtil";
import type { AxiosError } from "axios";
import { useUserService } from "~~/application/composables/service/useUserService";
import { useI18n } from "vue-i18n";
import type ProSkillAssureCandidateValidateResponse
  from "~~/package/apis/ProSkillAssure/response/ProSkillAssureCandidateValidateResponse";
import { UserOrgSettingsMenuType } from "~/components/User/Settings/Common/model/UserOrgSettingsMenuType";

const {t} = useI18n()

const router = useRouter();
const dialogStore = useDialogStore();
const { openWrongApproachAlert } = useGenericDialog();
const proSkillAssureService = useProSkillAssureService();
const userService = useUserService();

const isCheckValidEmailList = ref(false);
const isOpenLimitAlert = ref(false);
const isOpenSubscriptionErrorAlert = ref(false);
const isOpenDuplicateConfirm = ref(false);
const duplicateCount = ref(0);
const skippedEmails = ref<string[]>([]);

const emailList = ref<string[]>([]);
const emailInputData = ref("");
const options = ref(dialogStore.options.props);
const creditPerCandidate = ref(0);

const invitationContent = computed(() => convertToNextLine(t('skillCertify-invite-candidates-description')));

onMounted(async () => {
  const assessmentDetail = await proSkillAssureService.getProSkillAssureAssessmentDetail(options.value.assessmentId);
  creditPerCandidate.value = assessmentDetail.creditPerCandidate;
});

const alertContent = computed(() => {
  const linkDom = `<a class="link" href="/settings/org?tab=CreditManagement">${t("credit-certify-creation-limit-description-2")}</a>`
  return t("credit-certify-creation-limit-description-1", {linkDom});
})

const showDuplicateWarning = computed(() =>
  skippedEmails.value.length > 0 && skippedEmails.value.some(email => emailList.value.includes(email))
);

const isValidEmail = (email: string) => {
  return !emailValidation(email) && email.length > 0;
};

const isValidEmailList = (emailList: string[]) => {
  const isValid = emailList.filter(email => isValidEmail(email));
  isCheckValidEmailList.value = isValid.length === 0;
  if (!isCheckValidEmailList.value) {
    options.value.isOpenPopup(ToastType.FAILED, t('skillCertify-invite-candidates-email-invalid',{email: isValid[isValid.length - 1]}));
  }
};

const onKeyDownEnterEmailData = () => {
  if(!emailInputData.value.includes(",") && emailInputData.value.length > 0){
    emailList.value.push(emailInputData.value.trim());
    isValidEmailList(emailList.value);
  }
  emailInputData.value = "";
};

const onKeyUpCommasEmailData = () => {
  if(emailInputData.value.length <= 0) return;

  if(emailInputData.value.includes(',')){
    setEmailList(',');
  } else if(emailInputData.value.includes('\n')){
    setEmailList('\n');
  } else if(emailInputData.value.includes(' ')){
    setEmailList(' ');
  }
}

// ASMT-CAND-01: 콤마/개행/공백 분리 다건 추가 + Set 중복 자동제거 + 정규식 검증
const setEmailList = (splitKey: string) => {
  let splitEmailList = emailInputData.value.split(splitKey).filter(Boolean);
  splitEmailList = splitEmailList.filter((email) => email.trim() !== '');
  emailList.value = [...emailList.value, ...splitEmailList];
  const set = new Set(emailList.value);
  emailList.value = [...set];
  emailList.value = emailList.value.map(email => email.trim())
  isValidEmailList(emailList.value);
  emailInputData.value = "";
}

const onClickDeleteEmail = (index: number) => {
  emailList.value.splice(index, 1);
  isValidEmailList(emailList.value);
};

// ASMT-CREDIT-01: 초대 실행 — 서버가 신규 unique 인원만큼 차감, 성공 시 잔여 크레딧(userService.setUser) 갱신
const executeInvite = (list: string[], count: number): Promise<void> => {
  return proSkillAssureService.addProSkillAssureCandidateToAssessment(list, options.value.assessmentId)
    .then(isSuccess => {
      if (isSuccess) {
        options.value.isOpenPopup(
          ToastType.SUCCESS, t('skillCertify-invite-candidates-success', { count })
        );
        userService.setUser();
        onClickClose();
      } else {
        options.value.isOpenPopup(
          ToastType.FAILED, t('skillCertify-invite-candidates-fail', { count })
        );
      }
    })
    .catch((error: AxiosError) => handlerAddCandidateFailure(error, count));
};

// ASMT-CAND-02, ASMT-CREDIT-06: validate 미리보기로 기존 초대자(skipped) 감지 → 중복 안내 팝업 → 신규만 초대
const addCandidate = (): Promise<void> => {
  return new Promise((resolve) => {
    const count = emailList.value.length;
    if (count === 0 || !isCheckValidEmailList.value) return resolve();

    proSkillAssureService.validateProSkillAssureCandidates(options.value.assessmentId, emailList.value)
      .then((result: ProSkillAssureCandidateValidateResponse) => {
        if (result.skipped === 0) {
          executeInvite(emailList.value, count).finally(resolve);
        } else {
          skippedEmails.value = result.skippedEmails;
          duplicateCount.value = result.skipped;
          isOpenDuplicateConfirm.value = true;
          resolve();
        }
      })
      .catch(() => {
        openWrongApproachAlert();
        resolve();
      });
  });
}

const onClickDuplicateInvite = () => {
  const filtered = emailList.value.filter(email => !skippedEmails.value.includes(email));
  skippedEmails.value = [];
  executeInvite(filtered, filtered.length);
};

const onCloseDuplicateConfirm = () => {
  isOpenDuplicateConfirm.value = false;
};

const onConfirmDuplicateInvite = () => {
  isOpenDuplicateConfirm.value = false;
  onClickDuplicateInvite();
};

// PAY-GATE-06, ASMT-CREDIT-03: 초대 실패(402)를 catch해 구독 오류/초대 제한 팝업으로 표면화.
const handlerAddCandidateFailure = (error: AxiosError, count: number) => {
  if(error.response?.status === 402) {
    const errorCode = (error.response.data as any)?.data;
    if(errorCode === 'CP_1012') {
      isOpenSubscriptionErrorAlert.value = true;
    } else {
      isOpenLimitAlert.value = true;
    }
  } else {
    options.value.isOpenPopup(
      ToastType.FAILED, t('skillCertify-invite-candidates-fail', { count })
    );
  }
}

const onCloseLimitAlert = () => {
  isOpenLimitAlert.value = false;
}

const onConfirmLimitAlert = () => {
  userService.setUser();
  router.push({ path: '/settings/org', query: { tab: UserOrgSettingsMenuType.CreditManagement } });
}

const onCloseSubscriptionErrorAlert = () => {
  isOpenSubscriptionErrorAlert.value = false;
}

const onConfirmSubscriptionErrorAlert = () => {
  userService.setUser();
  router.push({ path: '/settings/org', query: { tab: UserOrgSettingsMenuType.PAYMENT } });
}

const onClickClose = () => {
  dialogStore.close();
}
</script>

<style>
.email.duplicate { border-color: #ff4444; color: #ff4444; }
.duplicateWarning { color: #ff4444; font-size: 12px; margin-top: 8px; }
</style>
